import { useEffect, useRef, useState } from "react";
import {
  AirPlaneIcon,
  CrossIcon,
  DocumentTextIcon,
  MapPinIcon,
  PlayCircleIcon,
  ShipIcon,
  SpinnerIcon,
} from "../../Icons";
import InputWithLabel from "../../InputWithLabel";
import SearchInput, { GeoNames } from "../../SearchInput";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import Dropdown from "../../DropDown";
import {
  CONTAINER_TYPE,
  TRADE_TERMS,
  TRANSPORTATION_BY,
} from "../../../constants/variable";
import Popover from "../Popover";

import { toast } from "react-toastify";
import HsCodeInput from "./HsCodeInput";
import { useTradeFlowData } from "../../../context/TradeFlowDataContext ";
import {
  useGetEstimatedQuoteMutation,
  useRequestLogisticsQuotesMutation,
} from "../../../store/apiSlices/logistics";
import EstimatedQuoteCardSkeleton from "../../Logistics/EstimatedQuoteCardSkeleton ";
import EstimatedQuoteCard from "../../Logistics/EstimatedQuoteCard";

const GET_QUOTES = [
  {
    label: "Taram",
    value: "Taram",
  },

  {
    label: "Add new vendor",
    value: "Add",
  },
];

export type ShipmentFormData = {
  from_json: Omit<GeoNames, "adminCode1" | "geonameId">;
  to_json: Omit<GeoNames, "adminCode1" | "geonameId">;
  freight_type: "ocean" | "air";
  transportation_by: string;
  pickup_date: string;
  weight: string;
  container_type: string;
  incoterm: string;
  goods_category: string;
  order_file: FileList;
  quotes_from: string;

  vendors: {
    name: string;
    email: string;
  }[];
  message: string;
};

export default function ShipmentBookingForm({
  shipmentBookingStepId,
}: {
  shipmentBookingStepId: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const {
    tradeFlowData: { row_id },
    refetch,
  } = useTradeFlowData();

  const [requestLogisticsQuotes, { isLoading: isRequestLoading }] =
    useRequestLogisticsQuotesMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    watch,
    control,
    unregister,
    reset,
  } = useForm<ShipmentFormData>({
    defaultValues: {
      freight_type: "ocean",
      transportation_by: "",
      container_type: "",
      weight: "",
      vendors: [{ email: "", name: "" }],
    },
  });

  const watchGetQuotesFrom = watch("quotes_from");
  const freight_type = watch("freight_type");

  useEffect(() => {
    if (watchGetQuotesFrom === "Taram") {
      unregister("vendors");
    }
  }, [watchGetQuotesFrom]);

  useEffect(() => {
    if (freight_type !== "ocean") {
      unregister(["transportation_by", "container_type"]);
    }
  }, [freight_type, unregister]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "vendors",
    rules: {
      maxLength: 4,
    },
  });

  const shippingLabel = () => {
    const freightType = getValues("freight_type");
    if (!freightType) return "Shipping Type";

    return [
      freightType,
      freightType === "ocean" && getValues("transportation_by"),
      freightType === "ocean" && getValues("container_type"),
      getValues("weight"),
    ]
      .filter(Boolean)
      .join(", ");
  };

  const [
    getEstimatedQuote,
    { isLoading, data: quotesData, reset: resetQuotesData },
  ] = useGetEstimatedQuoteMutation();

  const submitHandler = async (values: ShipmentFormData) => {
    // console.log(values);

    const payload = {
      from: `${values.from_json.name}, ${values.from_json.countryCode}`,
      to: `${values.to_json.name}, ${values.to_json.countryCode}`,
      type: values.freight_type,
      weight: values.weight,
    };

    await getEstimatedQuote(payload);

    window.dispatchEvent(new Event("scroll-to-bottom"));
  };

  // console.log(errors);

  const handleRequestQuotes = async (openSuccess: () => void) => {
    const values = getValues();

    const payload = {
      row_id,
      step_id: shipmentBookingStepId,
      ai_response: quotesData,
      ...values,
    };

    const { data } = await requestLogisticsQuotes(payload);

    if (data.status === "success") {
      reset();

      openSuccess();
      // refetch trade flow data to sync the RFQs
      refetch();
    } else {
      toast.error("Something went wrong! please try again.");
    }
  };

  return (
    <div className="space-y-4">
      <form
        className="flex gap-2 border p-3 rounded-lg"
        onSubmit={handleSubmit(submitHandler)}
      >
        <div className="w-full space-y-4">
          <div className="flex items-center gap-3">
            <Controller
              name="from_json"
              control={control}
              rules={{ required: "Please enter a source" }}
              render={({ field }) => (
                <div className="w-full">
                  <label
                    className="text-[#344054] text-sm font-medium"
                    htmlFor="from"
                  >
                    From
                  </label>
                  <SearchInput
                    placeholder="Source"
                    name="from"
                    initialCity={
                      field.value
                        ? `${field.value?.name}, ${field.value?.countryCode}`
                        : ""
                    }
                    icon={<MapPinIcon className="size-5 text-gray-700" />}
                    className="input-new !pl-[40px]"
                    onChange={field.onChange}
                  />

                  <small className="text-red-500 h-[10px] block ml-1">
                    {errors.from_json && errors.from_json.message}
                  </small>
                </div>
              )}
            />

            <Controller
              control={control}
              name="to_json"
              rules={{ required: "Please enter a destination" }}
              render={({ field }) => (
                <div className="w-full">
                  <label
                    className="text-[#344054] text-sm font-medium"
                    htmlFor="to"
                  >
                    To
                  </label>
                  <SearchInput
                    placeholder="Destination"
                    name="to"
                    initialCity={
                      field.value
                        ? `${field.value?.name}, ${field.value?.countryCode}`
                        : ""
                    }
                    icon={<MapPinIcon className="size-5 text-gray-700" />}
                    className="input-new !pl-[40px]"
                    onChange={field.onChange}
                  />

                  <small className="text-red-500 h-[10px] block ml-1">
                    {errors.to_json && errors.to_json.message}
                  </small>
                </div>
              )}
            />

            <InputWithLabel
              label="Pickup Date"
              type="date"
              onClick={(e) => {
                e.currentTarget.showPicker();
              }}
              {...register("pickup_date", {
                required: "Date is required",
              })}
              error={errors.pickup_date?.message}
            />

            <div className="relative w-full">
              <label className="text-[#344054] text-sm font-medium">
                Freight Type
              </label>
              {/* Trigger */}
              <div
                ref={triggerRef}
                className="input-new flex items-center gap-1 cursor-pointer border p-2 rounded-md"
                onClick={() => setOpen((prev) => !prev)}
              >
                {getValues("freight_type") === "ocean" && (
                  <ShipIcon className="size-5 text-gray-700" />
                )}
                {getValues("freight_type") === "air" && (
                  <AirPlaneIcon className="size-5 text-gray-700" />
                )}
                <p
                  className={`${
                    getValues("freight_type") ? "text-black" : "text-[#aaa]"
                  }`}
                >
                  <span className="line-clamp-1">{shippingLabel()}</span>
                </p>
              </div>

              <small className="text-red-500 h-[10px] block ml-1">
                {errors.freight_type ||
                errors.transportation_by ||
                errors.container_type ||
                errors.weight
                  ? "Select Freight Type"
                  : ""}
              </small>

              <Popover
                open={open}
                onClose={() => setOpen(false)}
                position="bottom"
                triggerRef={triggerRef}
                className="w-[300px] z-[60]"
              >
                {/* Freight Type */}
                <div className="mb-2">
                  <label className="block text-[#344054] text-sm font-medium">
                    Freight Type
                  </label>
                  <fieldset className="flex items-center gap-4">
                    <label
                      className={`flex items-center gap-2 px-3 py-2 rounded-2xl border shadow-sm transition cursor-pointer ${
                        freight_type === "ocean"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-black"
                      }`}
                    >
                      <input
                        type="radio"
                        value="ocean"
                        {...register("freight_type")}
                        className="sr-only"
                      />
                      <ShipIcon />
                      Ocean
                    </label>

                    <label
                      className={`flex items-center gap-2 px-3 py-2 rounded-2xl border shadow-sm transition cursor-pointer ${
                        freight_type === "air"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-black"
                      }`}
                    >
                      <input
                        type="radio"
                        value="air"
                        {...register("freight_type")}
                        className="sr-only"
                      />
                      <AirPlaneIcon />
                      Air
                    </label>
                  </fieldset>
                </div>

                {freight_type === "ocean" && (
                  <>
                    <Dropdown
                      label="Transportation By"
                      placeholder="Select Transportation By"
                      options={TRANSPORTATION_BY}
                      {...register("transportation_by", {
                        required: true,
                      })}
                    />
                    <Dropdown
                      label="Container Type"
                      placeholder="Select type"
                      options={CONTAINER_TYPE}
                      {...register("container_type", {
                        required: true,
                      })}
                    />
                  </>
                )}

                <InputWithLabel
                  label="Weight (Kgs)"
                  placeholder="Enter weight"
                  type="text"
                  {...register("weight", {
                    required: true,
                  })}
                />
              </Popover>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="w-full">
              <div className="flex items-center gap-3">
                <Controller
                  name="goods_category"
                  control={control}
                  rules={{ required: "Category is required" }}
                  render={({ field, fieldState }) => (
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Goods Category
                      </label>

                      <HsCodeInput
                        value={field.value}
                        onChange={field.onChange}
                      />

                      <small className="text-red-500 h-[10px] block ml-1">
                        {fieldState.error && fieldState.error.message}
                      </small>
                    </div>
                  )}
                />

                {/* <InputWithLabel
                  label="Order Information"
                  type="file"
                  {...register("order_file")}
                  // error={errors.orderFile?.message}
                /> */}

                <Dropdown
                  label="Incoterm"
                  placeholder="Select type"
                  options={TRADE_TERMS}
                  {...register("incoterm", {
                    required: "Incoterm is required",
                  })}
                  error={errors.incoterm?.message}
                />
              </div>
              <div>
                <label
                  htmlFor="email_messgae"
                  className="text-[#344054] text-sm font-medium"
                >
                  Email Message
                </label>
                <textarea
                  id="email_messgae"
                  className="input-new"
                  placeholder="Enter Message"
                  {...register("message", {
                    required: "Email message is required",
                  })}
                ></textarea>
                <small className="text-red-500 h-[10px] block ml-1">
                  {errors.message && errors.message.message}
                </small>
              </div>
            </div>

            <div className="w-full">
              <div className="flex gap-2 items-center">
                <Dropdown
                  label="Get Quotes From"
                  options={GET_QUOTES}
                  {...register("quotes_from")}
                />

                {watchGetQuotesFrom === "Add" && (
                  <button
                    type="button"
                    className="w-[40px] px-2 py-1 bg-custom-blue-medium text-white rounded"
                    onClick={() => append({ email: "", name: "" })}
                  >
                    +
                  </button>
                )}
              </div>

              {watchGetQuotesFrom === "Add" && (
                <div className="space-y-2">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex gap-2 items-end">
                      <InputWithLabel
                        label="Vendor name"
                        type="text"
                        placeholder="Enter here"
                        {...register(`vendors.${index}.name`, {
                          required: "Vendor name is required",
                        })}
                        error={errors.vendors?.[index]?.name?.message}
                      />

                      <InputWithLabel
                        label="Contact Information"
                        type="email"
                        placeholder="Enter email"
                        {...register(`vendors.${index}.email`, {
                          required: "Email is required",
                          pattern: {
                            value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                            message: "Invalid email format",
                          },
                        })}
                        error={errors.vendors?.[index]?.email?.message}
                      />

                      {fields.length !== 1 && (
                        <button
                          type="button"
                          className="text-red-500 mb-5"
                          onClick={() => remove(index)}
                        >
                          <CrossIcon className="size-6" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-[80px] flex flex-col justify-center items-center gap-4">
          {/* <button
            type="submit"
            className="p-1 rounded-lg hover:bg-gray-100 hover:scale-90 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            <DocumentTextIcon />
          </button> */}
          <button
            type="submit"
            className="p-1 text-custom-blue-medium rounded-lg hover:bg-gray-100 hover:scale-90 disabled:cursor-not-allowed disabled:text-custom-blue-medium/60"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600 inline-flex justify-center" />
            ) : (
              <PlayCircleIcon />
            )}
          </button>
        </div>
      </form>

      {isLoading ? (
        <EstimatedQuoteCardSkeleton />
      ) : (
        quotesData &&
        quotesData.success && (
          <EstimatedQuoteCard data={quotesData}>
            {(action) => (
              <>
                <EstimatedQuoteCard.Body>
                  <EstimatedQuoteCard.Header>
                    <EstimatedQuoteCard.Title />

                    <EstimatedQuoteCard.Action>
                      <EstimatedQuoteCard.Price />
                      <EstimatedQuoteCard.RequestBtn
                        handleClick={() =>
                          handleRequestQuotes(action.openSuccess)
                        }
                        isLoading={isRequestLoading}
                      />
                    </EstimatedQuoteCard.Action>
                  </EstimatedQuoteCard.Header>

                  <EstimatedQuoteCard.Route />
                  <EstimatedQuoteCard.Details />
                  <EstimatedQuoteCard.CostBreakdown />
                </EstimatedQuoteCard.Body>

                <EstimatedQuoteCard.SuccessDialog ref={action.dialogRef}>
                  <button
                    onClick={() => {
                      action.closeSuccess();
                      resetQuotesData();
                    }}
                    className="w-full py-2 bg-gray-200 rounded-lg"
                  >
                    Close
                  </button>
                </EstimatedQuoteCard.SuccessDialog>
              </>
            )}
          </EstimatedQuoteCard>
        )
      )}
    </div>
  );
}
